import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Camera trigger options per waypoint — Bramer-compatible. */
export type CameraAction =
  | 'NONE'
  | 'TAKE_PHOTO'
  | 'START_PHOTO_INTERVAL'
  | 'STOP_PHOTO_INTERVAL'
  | 'START_VIDEO'
  | 'STOP_VIDEO'
  | 'START_PHOTO_DISTANCE'
  | 'STOP_PHOTO_DISTANCE';

/** Per-waypoint vehicle action — Bramer-compatible. */
export type VehicleAction =
  | 'NONE'
  | 'TAKEOFF'
  | 'LAND'
  | 'TRANSITION_TO_FW'
  | 'TRANSITION_TO_MC';

export interface Waypoint {
  /** Sequence number, 0-based. */
  seq: number;
  lat: number;
  lon: number;
  /** Relative altitude above home (m). */
  alt_m: number;
  /** Seconds to hold at the waypoint. */
  hold_s?: number;
  /** Acceptance radius (m). */
  acceptance_radius_m?: number;
  /** Cruise speed along the leg leading TO this waypoint (m/s). */
  speed_m_s?: number;
  /** If true, pass through without braking. */
  is_fly_through?: boolean;
  /** Heading at the waypoint, degrees (0 = North). */
  yaw_deg?: number;
  /** Gimbal pitch (degrees; -90 = straight down). */
  gimbal_pitch_deg?: number;
  /** Gimbal yaw relative to vehicle heading. */
  gimbal_yaw_deg?: number;
  camera_action?: CameraAction;
  camera_photo_interval_s?: number;
  camera_photo_distance_m?: number;
  vehicle_action?: VehicleAction;
}

export interface GeofencePolygon {
  id: string;
  name?: string;
  /** Polygon vertices in order; closed implicitly. */
  points: Array<{ lat: number; lon: number }>;
}

export interface GeofenceCircle {
  id: string;
  name?: string;
  lat: number;
  lon: number;
  radius_m: number;
}

export interface PlanState {
  name: string;
  waypoints: Waypoint[];
  /** Fences (polygons + circles) for this mission. */
  fencePolygons: GeofencePolygon[];
  fenceCircles: GeofenceCircle[];
  /** Default values used when a new waypoint is added. */
  defaultAltM: number;
  defaultRadiusM: number;
  selectedSeq: number | null;
  /** Mission editor mode: 'waypoints' adds waypoints on map click; 'fence' draws polygon vertices. */
  editorMode: 'waypoints' | 'fence';
  /** Polygon vertices being drawn (interactive). Empty when not drawing. */
  pendingPolygon: Array<{ lat: number; lon: number }>;
}

interface MissionStore extends PlanState {
  setName: (n: string) => void;
  addWaypointAt: (lat: number, lon: number) => void;
  removeWaypoint: (seq: number) => void;
  moveWaypoint: (seq: number, lat: number, lon: number) => void;
  updateWaypoint: (seq: number, patch: Partial<Waypoint>) => void;
  reorderWaypoint: (fromSeq: number, toSeq: number) => void;
  setSelectedSeq: (seq: number | null) => void;
  setDefaults: (alt: number, radius: number) => void;
  setEditorMode: (m: 'waypoints' | 'fence') => void;
  addPolygonVertex: (lat: number, lon: number) => void;
  closePolygon: () => void;
  cancelPolygon: () => void;
  removePolygon: (id: string) => void;
  addCircleAt: (lat: number, lon: number) => void;
  updateCircle: (id: string, patch: Partial<GeofenceCircle>) => void;
  removeCircle: (id: string) => void;
  clear: () => void;
  loadFromJSON: (json: string) => void;
  toJSON: () => string;
}

const initial: PlanState = {
  name: 'Untitled mission',
  waypoints: [],
  fencePolygons: [],
  fenceCircles: [],
  defaultAltM: 15,
  defaultRadiusM: 2,
  selectedSeq: null,
  editorMode: 'waypoints',
  pendingPolygon: [],
};

function reseq(wps: Waypoint[]): Waypoint[] {
  return wps.map((w, i) => ({ ...w, seq: i }));
}

export const useMission = create<MissionStore>()(
  persist(
    (set, get) => ({
      ...initial,

      setName: (name) => set({ name }),

      addWaypointAt: (lat, lon) =>
        set((s) => {
          const wp: Waypoint = {
            seq: s.waypoints.length,
            lat,
            lon,
            alt_m: s.defaultAltM,
            acceptance_radius_m: s.defaultRadiusM,
          };
          return { waypoints: [...s.waypoints, wp], selectedSeq: wp.seq };
        }),

      removeWaypoint: (seq) =>
        set((s) => ({
          waypoints: reseq(s.waypoints.filter((w) => w.seq !== seq)),
          selectedSeq: null,
        })),

      moveWaypoint: (seq, lat, lon) =>
        set((s) => ({
          waypoints: s.waypoints.map((w) =>
            w.seq === seq ? { ...w, lat, lon } : w,
          ),
        })),

      updateWaypoint: (seq, patch) =>
        set((s) => ({
          waypoints: s.waypoints.map((w) =>
            w.seq === seq ? { ...w, ...patch } : w,
          ),
        })),

      reorderWaypoint: (fromSeq, toSeq) =>
        set((s) => {
          const arr = [...s.waypoints];
          const fi = arr.findIndex((w) => w.seq === fromSeq);
          const ti = arr.findIndex((w) => w.seq === toSeq);
          if (fi < 0 || ti < 0) return s;
          const [moved] = arr.splice(fi, 1);
          if (moved !== undefined) arr.splice(ti, 0, moved);
          return { waypoints: reseq(arr) };
        }),

      setSelectedSeq: (selectedSeq) => set({ selectedSeq }),

      setDefaults: (alt, radius) =>
        set({ defaultAltM: alt, defaultRadiusM: radius }),

      setEditorMode: (editorMode) =>
        set({ editorMode, pendingPolygon: editorMode === 'fence' ? [] : [] }),

      addPolygonVertex: (lat, lon) =>
        set((s) => ({ pendingPolygon: [...s.pendingPolygon, { lat, lon }] })),

      closePolygon: () =>
        set((s) => {
          if (s.pendingPolygon.length < 3) return s;
          const poly: GeofencePolygon = {
            id: `poly-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            points: s.pendingPolygon,
          };
          return {
            fencePolygons: [...s.fencePolygons, poly],
            pendingPolygon: [],
          };
        }),

      cancelPolygon: () => set({ pendingPolygon: [] }),

      removePolygon: (id) =>
        set((s) => ({ fencePolygons: s.fencePolygons.filter((p) => p.id !== id) })),

      addCircleAt: (lat, lon) =>
        set((s) => ({
          fenceCircles: [
            ...s.fenceCircles,
            {
              id: `circ-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              lat,
              lon,
              radius_m: 100,
            },
          ],
        })),

      updateCircle: (id, patch) =>
        set((s) => ({
          fenceCircles: s.fenceCircles.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        })),

      removeCircle: (id) =>
        set((s) => ({ fenceCircles: s.fenceCircles.filter((c) => c.id !== id) })),

      clear: () =>
        set({
          waypoints: [],
          fencePolygons: [],
          fenceCircles: [],
          pendingPolygon: [],
          selectedSeq: null,
        }),

      loadFromJSON: (json) => {
        try {
          const parsed = JSON.parse(json) as Partial<PlanState>;
          if (!parsed.waypoints || !Array.isArray(parsed.waypoints)) return;
          set({
            name: parsed.name ?? get().name,
            waypoints: reseq(parsed.waypoints as Waypoint[]),
            fencePolygons: parsed.fencePolygons ?? [],
            fenceCircles: parsed.fenceCircles ?? [],
            defaultAltM: parsed.defaultAltM ?? get().defaultAltM,
            defaultRadiusM: parsed.defaultRadiusM ?? get().defaultRadiusM,
            selectedSeq: null,
          });
        } catch (e) {
          console.error('mission load failed', e);
        }
      },

      toJSON: () => {
        const s = get();
        return JSON.stringify(
          {
            name: s.name,
            waypoints: s.waypoints,
            fencePolygons: s.fencePolygons,
            fenceCircles: s.fenceCircles,
            defaultAltM: s.defaultAltM,
            defaultRadiusM: s.defaultRadiusM,
          },
          null,
          2,
        );
      },
    }),
    { name: 'fabricos-mission-v1', version: 1 },
  ),
);
